/**
 * MCP Server for spacecode
 *
 * Exposes remote task execution tools via Model Context Protocol,
 * allowing programmatic dispatch and manage
 * remote tasks on cloud sandboxes.
 */

import { McpServer } from '@modelcontextprotocol/sdk/server/mcp.js';
import { z } from 'zod';

/**
 * Capture console output during a function call.
 *
 * Temporarily overrides console.log and console.error to collect
 * all output into a string buffer, then restores the originals.
 */
async function captureConsoleOutput(fn: () => Promise<void>): Promise<string> {
  const lines: string[] = [];
  const origLog = console.log;
  const origError = console.error;

  console.log = (...args: unknown[]) => {
    lines.push(args.map(String).join(' '));
  };
  console.error = (...args: unknown[]) => {
    lines.push(args.map(String).join(' '));
  };

  // Guard against process.exit() calls in the wrapped function.
  // Some commands (e.g. exec) call process.exit(1) on error,
  // which would kill the MCP server. We intercept it and throw instead.
  const origExit = process.exit;
  let exitCalled = false;
  let exitCode = 0;
  process.exit = ((code?: number) => {
    exitCalled = true;
    exitCode = code ?? 0;
    throw new Error(`process.exit(${exitCode})`);
  }) as never;

  try {
    await fn();
  } catch (error) {
    if (exitCalled) {
      lines.push(`[exited with code ${exitCode}]`);
    } else {
      lines.push(`Error: ${error instanceof Error ? error.message : String(error)}`);
    }
  } finally {
    console.log = origLog;
    console.error = origError;
    process.exit = origExit;
  }

  return lines.join('\n');
}

/**
 * Strip ANSI escape codes from a string for clean MCP output
 */
function stripAnsi(str: string): string {
  // eslint-disable-next-line no-control-regex
  return str.replace(/\u001B\[[0-9;]*m/g, '');
}

/**
 * Create and configure the MCP server with all remote task tools.
 */
export function createServer(): McpServer {
  const server = new McpServer({
    name: 'spacecode',
    version: '1.0.0',
  });

  // ---------- remote_exec ----------
  server.tool(
    'remote_exec',
    'Dispatch a task to a remote sandbox. Creates a Cloudflare container, clones the repo, and runs the coding agent with the given prompt. Blocks until the task completes and returns the result.',
    {
      repo: z.string().describe('GitHub repository (e.g. "owner/repo" or full URL)'),
      prompt: z.string().describe('The prompt / instructions to execute'),
      branch: z.optional(z.string().describe('Git branch to check out before running')),
    },
    async ({ repo, prompt, branch }) => {
      const execModule = await import('../commands/exec.js');

      const output = await captureConsoleOutput(async () => {
        await execModule.exec({
          repo,
          prompt,
          branch,
        });
      });

      return {
        content: [{ type: 'text', text: stripAnsi(output) }],
      };
    },
  );

  // ---------- remote_tasks ----------
  server.tool(
    'remote_tasks',
    'List remote tasks. By default shows only active (running/pending) tasks. Set all=true to include completed, failed, and cancelled tasks.',
    {
      all: z.optional(z.boolean().describe('Show all tasks including completed/cancelled. Default: false')),
    },
    async ({ all }) => {
      const { loadTasks } = await import('../config.js');
      const tasks = await loadTasks();

      if (tasks.length === 0) {
        return {
          content: [{ type: 'text', text: 'No tasks found.\n\nUse remote_exec to dispatch a new task.' }],
        };
      }

      const filtered = all
        ? tasks
        : tasks.filter(t => t.status === 'running' || t.status === 'pending');

      if (filtered.length === 0) {
        return {
          content: [{ type: 'text', text: 'No active tasks.\n\nUse all=true to show completed/cancelled tasks.' }],
        };
      }

      const lines: string[] = [`Tasks (${filtered.length}${all ? ' total' : ' active'}):\n`];

      for (const task of filtered) {
        lines.push(`  ${task.id}  [${task.status}]`);
        lines.push(`    repo:    ${task.repo}`);
        lines.push(`    prompt:  ${task.prompt.length > 80 ? task.prompt.substring(0, 77) + '...' : task.prompt}`);
        lines.push(`    mode:    ${task.mode}`);
        lines.push(`    created: ${task.createdAt}`);
        if (task.branch) {
          lines.push(`    branch:  ${task.branch}`);
        }
        if (task.prUrl) {
          lines.push(`    pr:      ${task.prUrl}`);
        }
        if (task.error) {
          lines.push(`    error:   ${task.error}`);
        }
        if (task.completedAt) {
          lines.push(`    done:    ${task.completedAt}`);
        }
        lines.push('');
      }

      const active = tasks.filter(t => t.status === 'running' || t.status === 'pending').length;
      lines.push(`${active} active / ${tasks.length} total tasks`);

      return {
        content: [{ type: 'text', text: lines.join('\n') }],
      };
    },
  );

  // ---------- remote_task_status ----------
  server.tool(
    'remote_task_status',
    'Get detailed status of a specific remote task by its ID.',
    {
      taskId: z.string().describe('The task ID (e.g. "task-abc123-xyz")'),
    },
    async ({ taskId }) => {
      const { getTask } = await import('../config.js');
      const task = await getTask(taskId);

      if (!task) {
        return {
          content: [{ type: 'text', text: `Error: Task "${taskId}" not found.` }],
          isError: true,
        };
      }

      return {
        content: [{ type: 'text', text: JSON.stringify(task, null, 2) }],
      };
    },
  );

  // ---------- remote_cancel ----------
  server.tool(
    'remote_cancel',
    'Cancel a running or pending remote task. Destroys the associated sandbox and marks the task as cancelled.',
    {
      taskId: z.string().describe('The task ID to cancel'),
    },
    async ({ taskId }) => {
      const { cancelTask } = await import('../commands/tasks.js');

      const output = await captureConsoleOutput(async () => {
        await cancelTask(taskId);
      });

      return {
        content: [{ type: 'text', text: stripAnsi(output) }],
      };
    },
  );

  // ---------- remote_exec_batch ----------
  server.tool(
    'remote_exec_batch',
    'Dispatch multiple tasks in parallel, each to its own remote Cloudflare container. Blocks until all tasks complete and returns structured results with task IDs and statuses.',
    {
      tasks: z.array(z.object({
        repo: z.string().describe('GitHub repository (e.g. "owner/repo")'),
        prompt: z.string().describe('Task instructions to execute'),
        branch: z.optional(z.string().describe('Git branch name. Auto-generated if omitted')),
      })).describe('Array of task specifications to dispatch in parallel'),
    },
    async ({ tasks: taskSpecs }) => {
      const { execBatch } = await import('../commands/exec-batch.js');

      try {
        const result = await execBatch(taskSpecs);

        const lines: string[] = [
          `Batch ${result.groupId}: ${result.dispatched}/${result.total} dispatched`,
          '',
        ];

        for (const task of result.tasks) {
          const icon = task.status === 'dispatched' ? 'OK' : 'FAIL';
          lines.push(`  [${icon}] ${task.taskId}`);
          lines.push(`       repo:   ${task.repo}`);
          lines.push(`       branch: ${task.branch}`);
          if (task.error) {
            lines.push(`       error:  ${task.error}`);
          }
          lines.push('');
        }

        if (result.failed > 0) {
          lines.push(`${result.failed} task(s) failed to dispatch.`);
        }

        lines.push('Use remote_wait_all to block until tasks complete.');

        return {
          content: [
            { type: 'text', text: lines.join('\n') },
            { type: 'text', text: JSON.stringify(result) },
          ],
        };
      } catch (error) {
        return {
          content: [{ type: 'text', text: `Error: ${error instanceof Error ? error.message : String(error)}` }],
          isError: true,
        };
      }
    },
  );

  // ---------- remote_wait_all ----------
  server.tool(
    'remote_wait_all',
    'Block until all specified remote tasks reach a terminal state (completed/failed/cancelled) or timeout. Returns structured summary with PR URLs, errors, and durations.',
    {
      taskIds: z.array(z.string()).describe('Array of task IDs to wait for'),
      timeoutMinutes: z.optional(z.number().describe('Max wait time in minutes (default: 30)')),
    },
    async ({ taskIds, timeoutMinutes }) => {
      const { waitForTasks } = await import('../commands/wait.js');

      try {
        const result = await waitForTasks(taskIds, timeoutMinutes ?? 30);

        const lines: string[] = [
          `Wait complete: ${result.completed} completed, ${result.failed} failed, ${result.cancelled} cancelled`,
        ];

        if (result.timedOut) {
          lines.push('WARNING: Timed out - some tasks may still be running.');
        }

        lines.push('');

        for (const task of result.tasks) {
          lines.push(`  ${task.taskId}  [${task.status}]`);
          lines.push(`    repo:     ${task.repo}`);
          if (task.branch) lines.push(`    branch:   ${task.branch}`);
          if (task.prUrl) lines.push(`    pr:       ${task.prUrl}`);
          if (task.error) lines.push(`    error:    ${task.error}`);
          if (task.durationSeconds != null) {
            const mins = Math.floor(task.durationSeconds / 60);
            const secs = task.durationSeconds % 60;
            lines.push(`    duration: ${mins}m ${secs}s`);
          }
          lines.push('');
        }

        return {
          content: [
            { type: 'text', text: lines.join('\n') },
            { type: 'text', text: JSON.stringify(result) },
          ],
        };
      } catch (error) {
        return {
          content: [{ type: 'text', text: `Error: ${error instanceof Error ? error.message : String(error)}` }],
          isError: true,
        };
      }
    },
  );

  // ---------- agent_create ----------
  server.tool(
    'agent_create',
    'Create a new AI agent service account with its own API key. The agent can authenticate and launch sessions independently. Returns the agent details and a one-time API key.',
    {
      name: z.string().regex(/^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/, 'Must be lowercase letters, digits, and hyphens; cannot start or end with a hyphen').describe('Agent name (lowercase, hyphens, e.g. "claude-ci")'),
      description: z.optional(z.string().describe('Purpose description for this agent')),
      webhookUrl: z.optional(z.string().describe('URL to receive webhook notifications on sandbox completion')),
    },
    async ({ name, description, webhookUrl }) => {
      const { createApiClient } = await import('../api/client.js');

      try {
        const client = await createApiClient();
        const result = await client.createAgent(name, { description, webhookUrl });

        const lines = [
          `Agent created: ${result.agent.profile.displayName}`,
          `  ID:      ${result.agent.id}`,
          `  Login:   ${result.agent.githubLogin}`,
          `  API Key: ${result.apiKey.token}`,
        ];
        if (result.agent.profile.webhookUrl) {
          lines.push(`  Webhook: ${result.agent.profile.webhookUrl}`);
        }
        lines.push('', 'Save the API key — it will not be shown again.');
        lines.push(`Usage: SPACECODE_TOKEN=${result.apiKey.token} spacecode -p "prompt" -R owner/repo`);

        return {
          content: [
            { type: 'text' as const, text: lines.join('\n') },
            { type: 'text' as const, text: JSON.stringify(result) },
          ],
        };
      } catch (error) {
        return {
          content: [{ type: 'text' as const, text: `Error: ${error instanceof Error ? error.message : String(error)}` }],
          isError: true,
        };
      }
    },
  );

  // ---------- agent_list ----------
  server.tool(
    'agent_list',
    'List all AI agent service accounts owned by the current user. Shows agent names, IDs, webhook URLs, and creation dates.',
    {},
    async () => {
      const { createApiClient } = await import('../api/client.js');

      try {
        const client = await createApiClient();
        const { agents } = await client.listAgents();

        if (agents.length === 0) {
          return {
            content: [{ type: 'text' as const, text: 'No agents found.\n\nUse agent_create to create a new agent service account.' }],
          };
        }

        const lines = [`Agents (${agents.length}):\n`];
        for (const a of agents) {
          lines.push(`  ${a.profile.displayName} (${a.id})`);
          lines.push(`    Login:   ${a.githubLogin}`);
          lines.push(`    Created: ${a.createdAt}`);
          if (a.profile.description) lines.push(`    Desc:    ${a.profile.description}`);
          if (a.profile.webhookUrl) lines.push(`    Webhook: ${a.profile.webhookUrl}`);
          lines.push('');
        }

        return {
          content: [
            { type: 'text' as const, text: lines.join('\n') },
            { type: 'text' as const, text: JSON.stringify({ agents }) },
          ],
        };
      } catch (error) {
        return {
          content: [{ type: 'text' as const, text: `Error: ${error instanceof Error ? error.message : String(error)}` }],
          isError: true,
        };
      }
    },
  );

  return server;
}
